import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CryptoJS from 'crypto-js';
import { useNavigate } from 'react-router-dom';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';
import { request } from '../../Utils/request';

type ChangePasswordResponse = {
  success?: boolean;
  message?: string;
};

const initialFormValues = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(initialFormValues);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasValidationError = useMemo(() => {
    if (!formValues.oldPassword.trim()) {
      return 'Please enter your current password.';
    }

    if (!formValues.newPassword.trim()) {
      return 'Please enter a new password.';
    }

    if (formValues.newPassword.trim().length < 6) {
      return 'New password must be at least 6 characters long.';
    }

    if (formValues.newPassword !== formValues.confirmPassword) {
      return 'New password and confirm password do not match.';
    }

    if (formValues.newPassword === formValues.oldPassword) {
      return 'New password must be different from the current password.';
    }

    return null;
  }, [formValues]);

  const handleInputChange = useCallback(
    (field: keyof typeof initialFormValues) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormValues((prev) => ({
          ...prev,
          [field]: event.target.value,
        }));
      },
    []
  );

  const togglePasswordVisibility = useCallback(
    (field: 'old' | 'new' | 'confirm') => () => {
      setShowPassword((prev) => ({
        ...prev,
        [field]: !prev[field],
      }));
    },
    []
  );

  const preventMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationMessage = hasValidationError;

    if (validationMessage) {
      setError(validationMessage);
      setSuccess(null);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }

      const hashPassword = (password: string, uniqueKey: string): string => {
        const hash = CryptoJS.HmacSHA256(password, uniqueKey);
        return hash.toString();
      };

      const payload = {
        oldPassword: hashPassword(formValues.oldPassword.trim(), 'ifinds'),
        newPassword: hashPassword(formValues.newPassword.trim(), 'ifinds'),
      };

      const response = await request<ChangePasswordResponse, typeof payload>(
        '/auth/change-password',
        {
          method: 'POST',
          body: payload,
          authorization: true,
        }
      );

      const successMessage =
        response?.message ?? 'Password changed successfully.';

      setSuccess(successMessage);
      setFormValues(initialFormValues);
      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/profile');
      }, 1500);
    } catch (apiError: any) {
      setError(apiError?.message ?? 'Unable to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ bgcolor: '#FFFFFF', minHeight: '100vh' }}>
      <TopNavBar title="Change Password" canGoBack />

      <Container maxWidth="sm" sx={{ py: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            borderRadius: 4,
            border: '1px solid #F4E3D5',
            backgroundColor: '#FFF7F2',
            p: 3,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ mb: 2, color: '#49454F', fontWeight: 600 }}
          >
            Update your password
          </Typography>

          {(error || success) && (
            <Alert
              severity={error ? 'error' : 'success'}
              onClose={() => {
                setError(null);
                setSuccess(null);
              }}
              sx={{ mb: 2 }}
            >
              {error ?? success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Old password"
              type={showPassword.old ? 'text' : 'password'}
              value={formValues.oldPassword}
              onChange={handleInputChange('oldPassword')}
              fullWidth
              margin="normal"
              required
              autoComplete="current-password"
              InputLabelProps={{ sx: { color: '#6750A4' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: '#FFFFFF',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword.old ? 'Hide old password' : 'Show old password'}
                      onClick={togglePasswordVisibility('old')}
                      onMouseDown={preventMouseDown}
                      edge="end"
                    >
                      {showPassword.old ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="New password"
              type={showPassword.new ? 'text' : 'password'}
              value={formValues.newPassword}
              onChange={handleInputChange('newPassword')}
              fullWidth
              margin="normal"
              required
              autoComplete="new-password"
              InputLabelProps={{ sx: { color: '#6750A4' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: '#FFFFFF',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword.new ? 'Hide new password' : 'Show new password'}
                      onClick={togglePasswordVisibility('new')}
                      onMouseDown={preventMouseDown}
                      edge="end"
                    >
                      {showPassword.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm password"
              type={showPassword.confirm ? 'text' : 'password'}
              value={formValues.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              fullWidth
              margin="normal"
              required
              autoComplete="new-password"
              InputLabelProps={{ sx: { color: '#6750A4' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: '#FFFFFF',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword.confirm ? 'Hide confirm password' : 'Show confirm password'}
                      onClick={togglePasswordVisibility('confirm')}
                      onMouseDown={preventMouseDown}
                      edge="end"
                    >
                      {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              sx={{
                mt: 3,
                borderRadius: 3,
                backgroundColor: '#F78002',
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#DB6D00',
                },
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: '#FFFFFF' }} />
                  Submitting
                </Box>
              ) : (
                'Submit'
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ChangePassword;

